import _Vue from 'vue';
import * as _ from 'lodash';
import ApolloClient from 'apollo-client';

import DeviceState from '@/interfaces/DeviceState';
import { lightIdsInGroupQuery } from '@/queries/group';
import { lightIdsInRoomQuery } from '@/queries/room';

export interface LightStateMachinePluginOptions {
  clearingPeriod: number;
  cacheIsOverUpdatesFor: number;
  // `apolloClient` is needed in situations when user pilots group or room.
  // We need to have an opportunity to distribute the current state among all
  // the lights in that room and group. With `apolloClient` we are able to
  // fetch all the lights for group/room from `apollo-cache-inmemory`
  apolloClient: ApolloClient<any>;
}

interface CacheRecord {
  state: DeviceState;
  cachedAt: Date;
}

export type DeviceType =
  | 'Light'
  | 'Group'
  | 'Room'
  | 'Floor'
  | 'Building';

export class LightStateMachine {

  private setIntervalId: number;

  private cacheMap: {[key: string]: CacheRecord} = {};

  constructor (
    private apolloClient: ApolloClient<any>,
    clearingPeriod: number,
    private cacheIsOverUpdatesFor: number,
  ) {
    this.setIntervalId = window.setInterval(
      () => this.removeOutdatedCacheRecords(),
      clearingPeriod,
    );
  }

  save(
    deviceType: DeviceType,
    deviceId: string,
    state: DeviceState,
  ) {
    switch (deviceType) {
    case 'Light':
      this.saveLight(deviceId, state);
      return;
    case 'Group':
      // Wrapped this call with try...catch because we don't want it to throw if
      // group or any other set of devices doesn't exist in the Apollo cache
      try {
        const lights = this.apolloClient.readQuery({
          query: lightIdsInGroupQuery,
          variables: { id: deviceId },
        }).group.lights;
        for (const light of lights) {
          this.saveLight(light.id, state);
        }
      } catch (e) {}
      return;
    case 'Room':
      // Wrapped this call with try...catch because we don't want it to throw if
      // room or any other set of devices doesn't exist in the Apollo cache
      try {
        const lights = this.apolloClient.readQuery({
          query: lightIdsInRoomQuery,
          variables: { id: deviceId },
        }).room.lights;
        for (const light of lights) {
          this.saveLight(light.id, state);
        }
      } catch (e) {}
      return;
    default:
      return;
    }
  }

  private saveLight(
    lightId: string,
    state: DeviceState,
  ) {
    const cachedRecord = this.cacheMap[lightId];
    // When we send the ON action to lights
    // we don't know the light mode it will be turned on with (in an arbitrary case).
    // It depends on WiZclick settings, and it could have night mode before switching off.
    // We push cache to ignore the state update and allow a future WS message to update the store.
    if (_.isEqual(state, { status: true })) {
      if (cachedRecord != undefined) {
        delete this.cacheMap[lightId];
      }
      return;
    }
    // We clone state before adding it to the cache to prevent cache changes outside the state machine
    const clonedState = _.cloneDeep(state);
    if (cachedRecord == undefined) {
      this.cacheMap[lightId] = {
        state: clonedState,
        cachedAt: new Date(),
      };
    } else {
      cachedRecord.state = LightStateMachine.getMergedState(cachedRecord.state, clonedState);
      cachedRecord.cachedAt = new Date();
    }
  }

  static getMergedState(
    currentState: DeviceState,
    updatedState: DeviceState,
  ): DeviceState {
    // We don't want to merge `currentState.mode` objects because it can lead to corrupted state in the cache
    // in case of changing LightModeScene to LightModeTemperature/LightModeColor.
    // e.g. { __typename: "scene", id: 10 } + { __typename: "temperature", temperature: 2700 } = { __typename: "temperature", id: 10, temperature: 2700 }
    // That tells to apollo cache to threat this object as entity instance what is not correct.
    const newState = _.merge(
      _.cloneDeep(currentState),
      _.omit(updatedState, 'mode'),
    );
    // We assume that if the user turns the light on after he turned it off
    // in `Night light` mode then he expects it to be turned on with `Daylight` mode.
    if (
      updatedState.status !== undefined
      && !updatedState.status
      && currentState.mode !== undefined
      && currentState.mode.__typename === 'LightModeScene'
      && currentState.mode.id == 14 // Night light
    ) {
      newState.mode = {
        __typename: 'LightModeScene',
        id: 12, // Daylight
      };
    } else if (updatedState.mode != undefined) {
      newState.mode = updatedState.mode;
    }
    return newState;
  }

  getState(
    lightId: string,
    updatedState: DeviceState,
  ): DeviceState {
    const cachedRecord = this.cacheMap[lightId];
    if (
      cachedRecord == undefined
      || this.isCacheRecordOutdated(cachedRecord)
    ) {
      return updatedState;
    }
    // We clone cached state before returning it to prevent cache changes outside the state machine
    const clonedCachedState = _.cloneDeep(cachedRecord.state);
    // We use `_.merge` (not `_.assign`) because we don't want to signal
    // about object changes during the update (if it isn't necessary)
    return _.merge(updatedState, clonedCachedState);
  }

  private isCacheRecordOutdated(record: CacheRecord): boolean {
    const now = new Date();
    return (
      record.cachedAt.getTime() + this.cacheIsOverUpdatesFor < now.getTime()
    );
  }

  private removeOutdatedCacheRecords() {
    this.cacheMap = _.omitBy(
      this.cacheMap,
      (record) => this.isCacheRecordOutdated(record),
    );
  }

}

export function LightStateMachinePlugin(Vue: typeof _Vue, options: LightStateMachinePluginOptions) {

  const stateMachine = new LightStateMachine(
    options.apolloClient,
    options.clearingPeriod,
    options.cacheIsOverUpdatesFor,
  );

  Vue.prototype.$lightStateMachine = stateMachine;

}
